import React, { useCallback, useEffect, useState } from "react";

// All images live in /app/frontend/public/gallery/.
// No captions/descriptions — purely visual. Alt text is generic for a11y only.
const PHOTOS = [
  "engineer.jpg",
  "lutrom-machines.jpg",
  "workshop.jpg",
  "assembled-taping-unit.jpg",
  "spindle-assembly.jpg",
  "taping-cage.jpg",
  "commissioning-cage.jpg",
  "fabrication-cross.jpg",
  "lathe.jpg",
  "machined-cup.jpg",
  "machined-part.jpg",
  "pulley-halves.jpg",
  "refurb-stand.jpg",
  "db-panel-2.jpg",
  "db-panel-3.jpg",
  "extra-01.jpg",
  "extra-02.jpg",
  "extra-03.jpg",
  "extra-04.jpg",
  "extra-05.jpg",
  "extra-06.jpg",
  "extra-07.jpg",
  "extra-08.jpg",
  "extra-09.jpg",
  "extra-10.jpg",
].map((f) => ({ src: `/gallery/${f}`, alt: "Lutrom Engineering workshop photo" }));

const Gallery = () => {
  const [openIdx, setOpenIdx] = useState(null);

  const close = useCallback(() => setOpenIdx(null), []);
  const next = useCallback(
    () => setOpenIdx((i) => (i === null ? null : (i + 1) % PHOTOS.length)),
    []
  );
  const prev = useCallback(
    () =>
      setOpenIdx((i) =>
        i === null ? null : (i - 1 + PHOTOS.length) % PHOTOS.length
      ),
    []
  );

  useEffect(() => {
    if (openIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [openIdx, close, next, prev]);

  return (
    <section
      className="relative mt-20 lg:mt-24"
      id="on-the-tools"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <div className="max-w-2xl mb-12">
          <p className="eyebrow">On the tools</p>
          <h2
            className="font-display mt-5 text-white"
            style={{ fontSize: "clamp(36px, 5vw, 60px)", lineHeight: 0.95 }}
          >
            FROM THE WORKSHOP
            <br />
            <span className="gradient-blue-text">TO THE SHOP FLOOR.</span>
          </h2>
          <p className="mt-5 text-[15px] leading-relaxed text-[color:var(--lt-text-dim)]">
            Real work, real engineers, real installs — a snapshot of recent
            Lutrom jobs across mechanical, electrical, fabrication and bespoke
            machinery.
          </p>
        </div>

        {/* CSS columns → true masonry, natural aspect ratios preserved */}
        <div
          className="gap-4"
          style={{
            columnGap: "1rem",
            columnCount: 1,
          }}
        >
          <style>{`
            .lutrom-masonry { column-count: 2; }
            @media (min-width: 768px) { .lutrom-masonry { column-count: 3; } }
            @media (min-width: 1280px) { .lutrom-masonry { column-count: 4; } }
            .lutrom-masonry > * { break-inside: avoid; -webkit-column-break-inside: avoid; page-break-inside: avoid; }
          `}</style>
          <div className="lutrom-masonry" style={{ columnGap: "1rem" }}>
            {PHOTOS.map((p, i) => (
              <button
                key={p.src}
                type="button"
                onClick={() => setOpenIdx(i)}
                className="group block w-full mb-4 rounded-xl overflow-hidden relative border border-white/5 bg-black/20 focus:outline-none focus:ring-2 focus:ring-[color:var(--lt-blue)]"
                data-testid={`gallery-photo-${i}`}
                aria-label="Open photo"
              >
                <img
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  className="w-full h-auto block transition-transform duration-500 ease-out group-hover:scale-105"
                  style={{ objectFit: "cover" }}
                />
                <span
                  className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(6,11,24,0) 55%, rgba(6,11,24,0.55) 100%)",
                  }}
                />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {openIdx !== null && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          style={{ background: "rgba(4,7,15,0.92)", backdropFilter: "blur(6px)" }}
          onClick={close}
          data-testid="gallery-lightbox"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              close();
            }}
            className="absolute top-5 right-5 w-11 h-11 rounded-full flex items-center justify-center text-white/80 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition"
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center text-white/80 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition"
            aria-label="Previous"
            data-testid="lightbox-prev"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 6l-6 6 6 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center text-white/80 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition"
            aria-label="Next"
            data-testid="lightbox-next"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 6l6 6-6 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <img
            src={PHOTOS[openIdx].src}
            alt={PHOTOS[openIdx].alt}
            onClick={(e) => e.stopPropagation()}
            className="max-w-[92vw] max-h-[86vh] object-contain rounded-lg shadow-2xl"
            data-testid="lightbox-image"
          />

          <div
            className="absolute bottom-5 left-1/2 -translate-x-1/2 text-xs tracking-[0.2em] text-white/60 font-mono"
            data-testid="lightbox-counter"
          >
            {String(openIdx + 1).padStart(2, "0")} / {String(PHOTOS.length).padStart(2, "0")}
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
